import {
  useGetLateNoticeSettings,
  useGetReminderSettings,
  useListNotifications,
  useSetLateNoticeSettings,
  useSetReminderSettings,
  type LateNoticeSettingsOut,
  type ReminderSettingsOut,
} from '@myrentalmate/api-client';
import {
  Alert,
  Button,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { okData, okItems } from '../../lib/apiData';
import { apiErrorMessage } from '../../lib/apiError';
import { MessageTable } from '../leases/MessageTable';
import { TablePager, useTablePage } from '../../components/TablePager';

function RemindersForm({ settings }: { settings: ReminderSettingsOut }) {
  const queryClient = useQueryClient();
  const [enabled, setEnabled] = useState(settings.enabled);
  const [before, setBefore] = useState(String(settings.days_before));
  const [after, setAfter] = useState(String(settings.days_after));
  const save = useSetReminderSettings({
    mutation: { onSuccess: () => queryClient.invalidateQueries() },
  });
  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Stack spacing={2} sx={{ alignItems: 'flex-start' }}>
        <Typography variant="h6">Rent Reminders</Typography>
        <Typography color="text.secondary">
          Emails residents before rent is due, and again if they still owe after the due date. They
          go to the email on each resident&apos;s record, and residents can turn them off in their
          portal.
        </Typography>
        {save.isError && <Alert severity="error">{apiErrorMessage(save.error)}</Alert>}
        {save.isSuccess && <Alert severity="success">Saved.</Alert>}
        <FormControlLabel
          label="Send Rent Reminders"
          control={<Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />}
        />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label="Days Before Rent Is Due"
            type="number"
            value={before}
            onChange={(e) => setBefore(e.target.value)}
            helperText="1 to 14"
            slotProps={{ htmlInput: { min: 1, max: 14 } }}
          />
          <TextField
            label="Days After, if Unpaid"
            type="number"
            value={after}
            onChange={(e) => setAfter(e.target.value)}
            helperText="1 to 20"
            slotProps={{ htmlInput: { min: 1, max: 20 } }}
          />
        </Stack>
        <Button
          variant="outlined"
          disabled={save.isPending}
          onClick={() =>
            save.mutate({
              data: { enabled, days_before: Number(before), days_after: Number(after) },
            })
          }
        >
          Save
        </Button>
      </Stack>
    </Paper>
  );
}

function LateNoticesForm({ settings }: { settings: LateNoticeSettingsOut }) {
  const queryClient = useQueryClient();
  const [enabled, setEnabled] = useState(settings.enabled);
  const [after, setAfter] = useState(String(settings.days_after));
  const save = useSetLateNoticeSettings({
    mutation: { onSuccess: () => queryClient.invalidateQueries() },
  });
  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Stack spacing={2} sx={{ alignItems: 'flex-start' }}>
        <Typography variant="h6">Late Notices</Typography>
        <Typography color="text.secondary">
          Sends a formal late notice once per due date to anyone who still owes, by email and in
          their portal, and keeps a copy under Notices. Unlike reminders, residents can&apos;t turn
          these off. Check your state&apos;s rules before turning this on.
        </Typography>
        {save.isError && <Alert severity="error">{apiErrorMessage(save.error)}</Alert>}
        {save.isSuccess && <Alert severity="success">Saved.</Alert>}
        <FormControlLabel
          label="Send Late Notices"
          control={<Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />}
        />
        <TextField
          label="Days After Rent Is Due"
          type="number"
          value={after}
          onChange={(e) => setAfter(e.target.value)}
          helperText="1 to 28"
          slotProps={{ htmlInput: { min: 1, max: 28 } }}
        />
        <Button
          variant="outlined"
          disabled={save.isPending}
          onClick={() => save.mutate({ data: { enabled, days_after: Number(after) } })}
        >
          Save
        </Button>
      </Stack>
    </Paper>
  );
}

export function NotificationSettingsPage() {
  const settings = okData(useGetReminderSettings().data);
  const late = okData(useGetLateNoticeSettings().data);
  const paging = useTablePage();
  const query = useListNotifications(paging.params);
  const messages = okItems(query.data);
  return (
    <Stack spacing={3}>
      <Stack spacing={3} sx={{ maxWidth: 720 }}>
        {settings && <RemindersForm settings={settings} />}
        {late && <LateNoticesForm settings={late} />}
      </Stack>
      <Stack spacing={1}>
        <Typography variant="h6">Recent Messages</Typography>
        {messages.length === 0 ? (
          <Typography color="text.secondary">No messages sent yet.</Typography>
        ) : (
          <>
            <MessageTable messages={messages} />
            <TablePager paging={paging} total={okData(query.data)?.total ?? 0} />
          </>
        )}
      </Stack>
    </Stack>
  );
}
